import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import AppStoreIntegrationsApi from '@/infrastructure/api/app-store-integrations'
import { useEcommerceId } from '@/hooks'
import { AppstoreintegrationspbSaveIntegrationCredentialsRequestStatusEnum } from '@itsrever/rever-api'

export const integrationKeys = {
  all: ['integrations'] as const,
  list: (ecommerceId: string) =>
    [...integrationKeys.all, 'list', ecommerceId] as const,
  details: (ecommerceId: string, integrationId?: string) =>
    [...integrationKeys.all, 'details', ecommerceId, integrationId] as const,
  connection: (ecommerceId: string, integrationId: string) =>
    [...integrationKeys.all, 'connection', ecommerceId, integrationId] as const
}

export function useAvailableIntegrations() {
  const ecommerceId = useEcommerceId()

  return useQuery({
    queryKey: integrationKeys.list(ecommerceId ?? ''),
    queryFn: async () => {
      if (!ecommerceId) throw new Error('ecommerceId is required')
      const response = await AppStoreIntegrationsApi.listAvailableIntegrations({
        ecommerceId
      })
      return response
    },
    enabled: !!ecommerceId
  })
}

export function useGetIntegrationDetails(integrationName?: string) {
  const ecommerceId = useEcommerceId()

  const {
    data: integrationDetails,
    isLoading,
    error
  } = useQuery({
    queryKey: integrationKeys.details(ecommerceId ?? '', integrationName),
    queryFn: async () => {
      if (!ecommerceId || !integrationName)
        throw new Error('ecommerceId is required')
      const response = await AppStoreIntegrationsApi.getIntegrationDetails({
        ecommerceId,
        integrationName
      })
      return response
    },
    enabled: !!ecommerceId && !!integrationName
  })

  return {
    integrationDetails,
    isLoading,
    error
  }
}

export function useSaveIntegrationCredentials() {
  const queryClient = useQueryClient()
  const ecommerceId = useEcommerceId()

  const mutation = useMutation({
    mutationFn: async ({
      integrationId,
      integrationName,
      credentials,
      status
    }: {
      integrationId: string
      integrationName: string
      ecommerceId: string
      credentials: Record<string, string>
      status: string
    }) => {
      if (!ecommerceId) throw new Error('ecommerceId is required')

      const payload = {
        appstoreintegrationspbSaveIntegrationCredentialsRequest: {
          integration_id: integrationId.toLowerCase(),
          integration_name: integrationName.toLowerCase(),
          ecommerce_id: ecommerceId.toLowerCase(),
          credentials: credentials,
          status:
            status as (typeof AppstoreintegrationspbSaveIntegrationCredentialsRequestStatusEnum)[keyof typeof AppstoreintegrationspbSaveIntegrationCredentialsRequestStatusEnum]
        }
      }

      const response =
        await AppStoreIntegrationsApi.saveIntegrationCredentials(payload)
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['integrationStatuses', ecommerceId]
      })

      queryClient.invalidateQueries({
        queryKey: integrationKeys.list(ecommerceId ?? '')
      })
    }
  })

  return mutation
}

const integrationStatusesOf = async (ecommerceId: string) => {
  const response = await AppStoreIntegrationsApi.getIntegrationStatus({
    ecommerceId
  })
  return response.integrations ?? []
}

// The integrations of a category active on any of the portals. The catalog is the same for all of them.
export function useActiveIntegrations(
  ecommerceIds: string[],
  category: string
) {
  const [first] = ecommerceIds
  const { data: integrations } = useQuery({
    queryKey: integrationKeys.list(first ?? ''),
    queryFn: () =>
      AppStoreIntegrationsApi.listAvailableIntegrations({ ecommerceId: first }),
    enabled: !!first
  })
  const statuses = useQueries({
    queries: ecommerceIds.map((ecommerceId) => ({
      queryKey: ['integrationStatuses', ecommerceId],
      queryFn: () => integrationStatusesOf(ecommerceId)
    }))
  })
  const active = new Set(
    statuses
      .flatMap(({ data }) => data ?? [])
      .filter((row) => row.status === 'INTEGRATION_STATUS_ACTIVE')
      .map((row) => row.name)
  )
  return (integrations ?? []).filter(
    (integration) =>
      integration.integration_category_tags?.includes(category.toLowerCase()) &&
      active.has(integration.integration_name)
  )
}

export function useGetIntegrationStatus() {
  const ecommerceId = useEcommerceId()

  const {
    data: integrationStatusData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['integrationStatuses', ecommerceId],
    queryFn: async () => {
      if (!ecommerceId) {
        throw new Error('ecommerceId is required')
      }
      return integrationStatusesOf(ecommerceId)
    },
    enabled: !!ecommerceId
  })

  return {
    integrationStatusData,
    isLoading,
    error
  }
}
